import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import {NgxPermissionsModule} from 'ngx-permissions';
import {Observable} from 'rxjs';
import {UserService} from '../../services/user.service';
import {UserVM} from '../../models/user/user-vm.model';
import {environment} from '../../../environments/environment';
import {filter, tap} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';
import {OrgScopeStore} from '../../services/org-scope-store';
import {TicketsStore} from '../../ticketing/services/tickets.store';

@Component({
    selector: 'app-footer',
    imports: [CommonModule, RouterModule, NgxPermissionsModule],
    templateUrl: './footer.component.html',
    styleUrls: ['./footer.component.scss']
})
export class FooterComponent implements OnInit {

  public currentUser$: Observable<UserVM>;
  public companyName: string;
  private userService = inject(UserService);
  private ticketsStore = inject(TicketsStore);

  /** Tickets waiting on this user. The same count the Tickets tab shows. */
  public ticketsNeedingInput = this.ticketsStore.needsYourInputCount;
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
   this.userService.getUserCompaniesAsObs().pipe(
     tap(userCompanies => {
       const managedCompany = +(OrgScopeStore.getItem('managedCompanyId') ?? 0);
       if (managedCompany) {
         this.companyName = userCompanies.find((company) => company.id === managedCompany)?.name;
       }
     })
   ).subscribe();
   this.currentUser$ = this.userService.getCurrentUserAsObs();

   // The ticket count, refreshed on navigation rather than on a timer: the footer is on every screen,
   // so a poll would be a request every few seconds for a number that changes rarely, while navigating
   // is exactly when a stale count would be noticed. The store swallows failures — a count that cannot
   // be fetched leaves the last known value rather than putting an error in the footer.
   this.ticketsStore.refreshSummary();
   this.router.events.pipe(
     filter(event => event instanceof NavigationEnd),
     takeUntilDestroyed(this.destroyRef),
   ).subscribe(() => this.ticketsStore.refreshSummary());
  }

  protected readonly environment = environment;

  async openCyprusRegistrySearch(): Promise<void> {
    // Lazy-load so jsPDF + xlsx (pulled in by this dialog) stay out of the eager bundle.
    const {CyprusRegistrySearchComponent} = await import('../../home/cyprus-registry-search/cyprus-registry-search.component');
    this.dialog.open(CyprusRegistrySearchComponent, {
      width: '960px',
      maxWidth: '95vw',
      autoFocus: false
    });
  }
}
